import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import { useRef, useState, useEffect } from "react";
import { api, resolveMedia } from "../lib/api";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Image as ImageIcon,
  Quote,
  List,
  ListOrdered,
  Minus,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

/**
 * RichTextEditor — editorial rich-text editor built on TipTap.
 * Supports bold / italic / underline, H2 / H3 section breaks, hyperlinks,
 * inline images (uploaded via the existing /api/media/upload endpoint),
 * blockquotes, bullet + ordered lists, and a "sans accent" span class for
 * font switching inside a paragraph.
 */
export default function RichTextEditor({ value, onChange, placeholder }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // If value is plain text (no HTML tags), coerce it into paragraphs so
  // existing articles open naturally in the editor without losing line breaks.
  const initial = (() => {
    const v = value || "";
    if (/<[a-z][\s\S]*>/i.test(v)) return v;
    return v
      .split(/\n+/)
      .map((p) => `<p>${escapeHtml(p)}</p>`)
      .join("");
  })();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Image.configure({ inline: false, allowBase64: false }),
    ],
    content: initial,
    editorProps: {
      attributes: {
        class:
          "prose-editorial focus:outline-none min-h-[260px] px-4 py-4 bg-white",
      },
    },
    onUpdate({ editor }) {
      onChange?.(editor.getHTML());
    },
  });

  // Keep editor in sync when parent swaps `value` (e.g. switching to a new
  // article from the list). We only reset when the incoming value differs
  // from the current HTML; otherwise we'd clobber the user's typing.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (!incoming && !current) return;
    if (incoming === current) return;
    // Only reset on a genuine external change — not on each keystroke.
    const incomingNormalized = /<[a-z][\s\S]*>/i.test(incoming)
      ? incoming
      : incoming
          .split(/\n+/)
          .map((p) => `<p>${escapeHtml(p)}</p>`)
          .join("");
    if (incomingNormalized !== current) {
      editor.commands.setContent(incomingNormalized, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const toggleLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL del enlace (deje vacío para quitar):", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    const safe = /^(https?:|mailto:|tel:)/i.test(url) ? url : `https://${url}`;
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: safe, target: "_blank", rel: "noopener noreferrer" })
      .run();
  };

  const insertImage = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const res = await api.post("/media/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data.url;
      if (!url) throw new Error("Upload returned no url");
      editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      toast.success("Foto insertada en el cuerpo");
    } catch (err) {
      toast.error("No se pudo subir la foto");
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Font accent: wrap/unwrap selection in a <span class="rt-sans"> for
  // an Inter sans-serif callout inside the serif body.
  const toggleSansAccent = () => {
    const { from, to } = editor.state.selection;
    if (from === to) {
      toast.message("Seleccione el texto que quiere cambiar.");
      return;
    }
    const selected = editor.state.doc.textBetween(from, to, " ");
    editor
      .chain()
      .focus()
      .insertContentAt({ from, to }, `<span class="rt-sans">${escapeHtml(selected)}</span>`)
      .run();
  };

  const btn = (active) =>
    `px-2.5 py-1.5 text-ink border border-border2 bg-white hover:bg-sand transition ${
      active ? "bg-cobalt/10 border-cobalt text-cobalt" : ""
    }`;

  return (
    <div className="mt-2 border border-border2 bg-white" data-testid="rich-text-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 border-b border-border2 bg-sand/50 p-2 sticky top-0 z-10">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={btn(editor.isActive("bold"))}
          title="Negrita (Cmd/Ctrl+B)"
          data-testid="rt-bold"
        >
          <BoldIcon size={14} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={btn(editor.isActive("italic"))}
          title="Cursiva (Cmd/Ctrl+I)"
          data-testid="rt-italic"
        >
          <ItalicIcon size={14} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={btn(editor.isActive("underline"))}
          title="Subrayado"
          data-testid="rt-underline"
        >
          <UnderlineIcon size={14} />
        </button>

        <span className="w-px bg-border2 mx-1" />

        <select
          value={
            editor.isActive("heading", { level: 2 })
              ? "h2"
              : editor.isActive("heading", { level: 3 })
              ? "h3"
              : "p"
          }
          onChange={(e) => {
            const v = e.target.value;
            if (v === "p") editor.chain().focus().setParagraph().run();
            if (v === "h2") editor.chain().focus().toggleHeading({ level: 2 }).run();
            if (v === "h3") editor.chain().focus().toggleHeading({ level: 3 }).run();
          }}
          className="px-2 py-1.5 text-xs border border-border2 bg-white"
          title="Estilo de párrafo"
          data-testid="rt-style"
        >
          <option value="p">Párrafo</option>
          <option value="h2">Encabezado · H2</option>
          <option value="h3">Encabezado · H3</option>
        </select>

        <button
          type="button"
          onClick={toggleSansAccent}
          className={btn(false)}
          title="Acento sans-serif (Inter)"
          data-testid="rt-sans"
        >
          <span className="font-sans text-[0.7rem] tracking-widest uppercase">Aa</span>
        </button>

        <span className="w-px bg-border2 mx-1" />

        <button
          type="button"
          onClick={toggleLink}
          className={btn(editor.isActive("link"))}
          title="Insertar enlace"
          data-testid="rt-link"
        >
          <LinkIcon size={14} />
        </button>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className={btn(false)}
          title="Insertar foto"
          data-testid="rt-image"
        >
          <ImageIcon size={14} />
          {uploading && <span className="ml-1 text-[0.65rem]">…</span>}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={(e) => insertImage(e.target.files?.[0])}
          className="hidden"
        />

        <span className="w-px bg-border2 mx-1" />

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={btn(editor.isActive("bulletList"))}
          title="Viñetas"
          data-testid="rt-ul"
        >
          <List size={14} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={btn(editor.isActive("orderedList"))}
          title="Lista numerada"
          data-testid="rt-ol"
        >
          <ListOrdered size={14} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={btn(editor.isActive("blockquote"))}
          title="Cita"
          data-testid="rt-quote"
        >
          <Quote size={14} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          className={btn(false)}
          title="Separador"
          data-testid="rt-hr"
        >
          <Minus size={14} />
        </button>

        <span className="w-px bg-border2 mx-1" />

        <button
          type="button"
          onClick={() =>
            editor.chain().focus().unsetAllMarks().clearNodes().run()
          }
          className={btn(false)}
          title="Quitar formato"
          data-testid="rt-clear"
        >
          <RotateCcw size={14} />
        </button>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
}

function escapeHtml(s) {
  return (s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
